/**
 * The one home for "write one message into this conversation's caches".
 *
 * A write that has landed shows up in the same frame, instead of when its refetch does.
 * That is what lets every dialog ordering the write close on the WRITE: the refresh still
 * runs, as the authority that settles what a patch could not know, but nothing waits on it.
 *
 * It iterates `getQueriesData` rather than calling `setQueriesData` because the patch has
 * to see the query KEY — a `setQueriesData` updater never does, and the pinned strip is a
 * server-filtered list that needs the opposite treatment from the thread
 * (`isPinnedOnlyMessagesKey`). `_libs/inbox-read-patch.ts` hand-rolls the same loop, for
 * the same reason.
 */

import { queryKeys } from "@birdwatch/client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  type CachedMessages,
  isPinnedOnlyMessagesKey,
  withPatchedMessage,
} from "../_libs/message-cache";
import type { ChannelMessage } from "./channel-endpoints";

/**
 * What to do with the message in one cached query. `pinnedOnly` says it is the pinned
 * strip's list. Returning `null` drops the message from that cache.
 */
export type ChannelMessagePatch = (
  message: ChannelMessage,
  context: { pinnedOnly: boolean },
) => ChannelMessage | null;

/**
 * Returns a stable `patchMessage(messageId, patch)` covering every cached query of `cid` —
 * the infinite thread, the pinned strip, search results and a reply thread, which all sit
 * under the `getChannelsMessagesByCid({ cid })` key prefix.
 */
export function useChannelMessageCache(cid: string) {
  const queryClient = useQueryClient();

  return useCallback(
    (messageId: string, patch: ChannelMessagePatch) => {
      if (!cid) return;

      const entries = queryClient.getQueriesData<
        CachedMessages<ChannelMessage>
      >({
        queryKey: queryKeys.getChannelsMessagesByCid({ cid }),
      });

      for (const [key, cached] of entries) {
        const next = withPatchedMessage(cached, messageId, (message) =>
          patch(message, { pinnedOnly: isPinnedOnlyMessagesKey(key) }),
        );
        // Same reference back means this cache did not hold the message. Skipping the
        // write is what keeps `dataUpdatedAt` still and the other threads unrendered.
        if (next !== cached) queryClient.setQueryData(key, next);
      }
    },
    [queryClient, cid],
  );
}
